import { CloneProjectState } from "@app/projects/clone/cloneProjectState";
import CreateProject from "@app/projects/create/createProject";
import { FilterType } from "@app/projects/enumerators";
import { ProjectOptionsState } from "@app/projects/network-assessment/projectOptionsState";
import Project from "@app/projects/project";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import moment from "moment/moment";

/* oxlint-disable sort-keys */
/* These keys are listed in order of where they appear in the edit project UI */
/* This method is specifically for when cloning projects */
export const cloneThingsToResetIfZipProject = (isZipTemplate: boolean): Partial<ProjectOptionsState | CloneProjectState> => {
  return isZipTemplate ? {
    // template step
    selectedInsurances: [],
    useAccuracy: false,
    // member step
    disableMemberBindingTable: true,
    disableMarketProjectionCopy: true,
    disableProspectProjectionCopy: true,
    disableTerminationCopy: true,
    // options step
    // : exclude prospect/market providers
    excludeDeactivatedFacilityImpact: false,
    excludeDeactivatedProviderImpact: false,
    excludeInvalidNppesNpiFacilityImpact: false,
    excludeInvalidNppesNpiProviderImpact: false,
    excludeMedicareOptOutImpact: false,
    excludeMissingNpiFacilityImpact: false,
    excludeMissingNpiProviderImpact: false,
    excludeOIGExclusionImpact: false,
    excludePrecludedImpact: false,
  } : {};
}

/* This method is specifically for when cloning projects */
export const cloneProjectThingsToResetIfZipProject = (isZipTemplate: boolean, isZipVolumeTargetingEnabled: boolean = false, isZipGapAgeTrackingEnabled: boolean = false): Partial<Project> => {
  return isZipTemplate ? {
    // template step
    accuracySpecialtyDefinitionId: null,
    accuracySpecialtyToQdpCodeMapSetId: null,
    accuracyTemplateCollectionId: null,
    accuracyTemplateCorrelationId: "",
    marketSpecialtyCodeMapSetId: null,
    // members step
    memberSetAlias2: "",
    memberSetId2: null,
    membershipRiskMemberSetAlias: "",
    membershipRiskMemberSetId: null,
    // step providers
    marketProviderSetAlias: "",
    marketProviderSetId: null,
    prospectProviderSetAlias: "",
    prospectProviderSetId: null,
    useMarketProvidersForImpact: true, // we won't have market providers, but this is the default
    useMarketProvidersForMarketAnalysis: false,
    useNetworkProvidersForMarketAnalysis: false,
    useProspectProvidersForMarketAnalysis: false,
    // options step
    // : analysis
    ...(isZipVolumeTargetingEnabled ? {} : { excludeInVolumeActions: true }),
    includeInEnterpriseComplianceDashboard: false,
    autoArchiveUnarchiveSpecialtyExceptions: false,
    showAccuracyGapAnalysis: false,
    showCMSCredit: false,
    strictTimeAndDistance: true,
    // : display options
    hasZipSummaries: false,
    // ZipGapAgeTracking makes Show Gap Aging Analysis apply to ZIP projects
    ...(isZipGapAgeTrackingEnabled ? {} : { includeGapAging: false }),
    showAttestation: false,
    showAdvancedComparison: false,
    showClosestProviderReport: false,
    showNextClosestNetworkProviders: false,
  } : {
    ncpInStateProvidersOnly: false,
  };
}

// values pasted from initial state in constructor
/* oxlint-disable sort-keys */
/* This method is specifically for when creating projects */
export const createProjectThingsToResetIfZipProject = (isZipTemplate: boolean, isZipVolumeTargetingEnabled: boolean = false, isZipGapAgeTrackingEnabled: boolean = false): Partial<CreateProject> => {
  return isZipTemplate ? {
    // template step
    accuracyAndAdequacy: false,
    accuracySpecialtyDefinitionDesc: null,
    accuracySpecialtyDefinitionId: null,
    accuracySpecialtyToQdpCodeMapSetDescription: null,
    accuracySpecialtyToQdpCodeMapSetId: null,
    accuracyTemplateCollectionDescription: "",
    accuracyTemplateCollectionId: null,
    accuracyTemplateCorrelationId: "",
    marketSpecialtyCodeMapSetId: null,
    // members step
    memberPopDescription2: "",
    memberSetAlias2: "",
    memberSetId2: null,
    membershipRiskMemberSetAlias: "",
    membershipRiskMemberSetId: null,
    // step providers
    marketProviderSetAlias: "",
    marketProviderDescription: "",
    marketProviderSetId: null,
    prospectProviderSetAlias: "",
    prospectProviderDescription: "",
    prospectProviderSetId: null,
    useMarketProvidersForImpact: true, // we won't have market providers, but this is the default
    useMarketProvidersForMarketAnalysis: false,
    useNetworkProvidersForMarketAnalysis: false,
    useProspectProvidersForMarketAnalysis: false,
    // options step
    // : analysis
    ...(isZipVolumeTargetingEnabled ? {} : { excludeInVolumeActions: true }),
    includeInEnterpriseComplianceDashboard: false,
    autoArchiveUnarchiveSpecialtyExceptions: false,
    showAccuracyGapAnalysis: false,
    showCMSCredit: false,
    strictTimeAndDistance: true,
    // : display options
    hasZipSummaries: false,
    // ZipGapAgeTracking makes Show Gap Aging Analysis apply to ZIP projects
    ...(isZipGapAgeTrackingEnabled ? {} : { includeGapAging: false }),
    showAttestation: false,
    showAdvancedComparison: false,
    showClosestProviderReport: false,
    showNextClosestNetworkProviders: false,
    showPatternOfCareProviders: false,
    showPciDashboard: false,
    // : exclude prospect/market providers
    excludeDeactivatedFacilitiesImpact: false,
    excludeDeactivatedProvidersImpact: false,
    excludeInvalidNppesNpiFacilitiesImpact: false,
    excludeInvalidNppesNpiProvidersImpact: false,
    excludeMedicareOptOutProvidersImpact: false,
    excludeMissingNpiFacilitiesImpact: false,
    excludeMissingNpiProvidersImpact: false,
    excludeOIGExcludedProvidersImpact: false,
    excludePrecludedProvidersImpact: false,
    networkSpecialtyCodeMapSetId: null
  } : {
    ncpInStateProvidersOnly: false,
  };
}
/* oxlint-enable sort-keys */

// If we're comparing run configs, the left arc reactor is always the non-termed/projected/projected-termed run config,
// and if they're both non-termed/projected/projected-termed, the left arc reactor is the one with the earlier timestamp.
// We also want the projected/termed/projected-termed slices in that order if we're comparing them.
export const shouldSwitchProjectRunConfigurationsInCompareMode = (firstConfig: ProjectRunConfigurationResult, secondConfig: ProjectRunConfigurationResult): boolean => {
  // Don't switch if we don't have a secondConfig.
  if (!secondConfig) {
    return false;
  }

  // If both configs are termed/projected/projected-termed, enforce the ordering projected > termed > projected-termed to match the trend chart.
  if (isRunConfigProjectedTermedOrProjectedTermed(firstConfig) && isRunConfigProjectedTermedOrProjectedTermed(secondConfig)) {
    return !(firstConfig.isProjected || (firstConfig.isTermed && secondConfig.isProjectedTermed));
  }

  const firstRunConfigTimestamp = moment.utc(firstConfig && firstConfig.dateCreated);
  const secondRunConfigTimestamp = moment.utc(secondConfig && secondConfig.dateCreated);
  // Switch the secondConfig to be first if it's before the firstConfig AND not termed/projected/projected-termed.
  return (secondRunConfigTimestamp.isBefore(firstRunConfigTimestamp) && !isRunConfigProjectedTermedOrProjectedTermed(secondConfig)) ||
    // Also switch if the firstConfig is termed/projected/projected-termed.
    isRunConfigProjectedTermedOrProjectedTermed(firstConfig)
}

const isRunConfigProjectedTermedOrProjectedTermed = (runConfig: ProjectRunConfigurationResult): boolean => {
  return runConfig.isProjectedTermed || runConfig.isProjected || runConfig.isTermed;
}

export const countyFilterPathRegEx = new RegExp(`${FilterType.County}[^/]*`);